import { computed, makeAutoObservable } from 'mobx';
import { AvProxyApi } from '@availity/api-axios';

import { getSpacesApi, getCurrentRegions, getUser } from '../../api/availity/api-availity';

import hashcode from '../../utilities/hashcode';
import urlSearch from '../../utilities/url';
import { getActiveCodeValuesByPath } from '../../utilities/util';

import flClearLogo from '../../logos/public_fl_clear.png';
import flSimplyLogo from '../../logos/public_fl_simply.png';
import public_ks from '../../logos/public_ks.png';
import public_mo from '../../logos/public_mo.png';
import public_nc from '../../logos/public_nc.png';
import public_ne from '../../logos/public_ne.png';
import public_oh from '../../logos/public_oh.png';
import public_co from '../../logos/public_co.png';
import public_wi from '../../logos/public_wi.png';

class CommonStore {
  appName = '';

  appVersion = '';

  brand = {};

  termsOfUse;

  user = {};

  blocking = false;

  config = {};

  dynamicConfig = {};

  templates = {};

  dynamicFieldsConfig = [];

  deliveryChannel = '';

  bucket = '';

  payerId = '';

  qocID = '';

  groupID = '';

  claimID = '';

  providerID = '';

  inPublicfacingFlow = false;

  stateForPublicFlow = '';

  brandForPublicFlow = '';

  publicImgHref = '';

  GEOJSON_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

  logos = {
    FL: [
      { id: 'clear', logo: flClearLogo },
      { id: 'simply', logo: flSimplyLogo },
    ],
    KS: [{ id: 'healthyBlue', name: 'Healthy Blue', logo: public_ks }],
    MO: [{ id: 'healthyBlue', name: 'Healthy Blue', logo: public_mo }],
    NC: [{ id: 'healthyBlue', name: 'Healthy Blue', logo: public_nc }],
    NE: [{ id: 'healthyBlue', name: 'Healthy Blue', logo: public_ne }],
    OH: [{ id: 'anthembcbs', name: 'Anthem Blue Cross Blue Shield', logo: public_oh }],
    CO: [{ id: 'COCHA', name: 'COCHA', logo: public_co }],
    WI: [{ id: 'anthembcbs', name: 'Anthem Blue Cross Blue Shield', logo: public_wi }],
  };

  appStateBrandReportConfig = {};

  states = [];

  brandsByState = [];

  reportTypes = [];

  constructor() {
    makeAutoObservable(this);
  }

  @computed get userAndBrand() {
    const userAndBrand = {};
    userAndBrand.brand = this.brand;
    userAndBrand.user = this.user;
    return userAndBrand;
  }

  assignConfig = (config = {}) => {
    for (const key of Object.keys(config)) {
      this[key] = config[key] ?? '';
    }
    this.dynamicFieldsConfig = config.dynamicFieldNames ?? [];
    this.config = config;
  };

  getSpacesApi = async () => {
    const queryString = urlSearch();
    const { spaceId, appId } = queryString;

    try {
      const response = await getSpacesApi(spaceId);
      const { metadata, name, images } = response || {};
      this.brand.code = metadata.brandCode;
      this.brand.name = name;
      this.brand.payerName = name;
      this.brand.logoURL = images.logo;
      this.brand.spaceId = spaceId;
      this.brand.appId = appId || '';
      return true;
    } catch {
      return false;
    }
  };

  async getCurrentRegion() {
    try {
      const response = await getCurrentRegions();
      this.brand.state = response.currentRegion[0].state;
      return true;
    } catch {
      return false;
    }
  }

  async getUser() {
    try {
      const user = await getUser();
      this.user = user;
      this.user.interactionId = hashcode.getHashCode(this.user.id + Date.now());
      return true;
    } catch {
      return false;
    }
  }

  async getTermsOfUse() {
    const { state, code } = this.brand;
    const getTermsOfUseResource = new AvProxyApi({
      tenant: 'anthem',
      name: 'v1/providerapp/utilities/disclaimer',
    });
    const response = await getTermsOfUseResource.post({
      state,
      brand: code,
      application: '10006',
      parameters: [],
    });
    if (!response.data?.disclaimerInfo) return false;

    this.termsOfUse = response.data?.disclaimerInfo?.component?.disclaimer || '';

    return true;
  }

  callPrvprtlConfigApiForAppConfig = async () => {
    const prvprtlConfigApiApiResource = new AvProxyApi({
      tenant: 'anthem',
      name: `/v1/providerapp`,
    });
    return prvprtlConfigApiApiResource
      .get('reportanincident.config')
      .then((response) => {
        if ((!response.error || !response.exceptions) && response && response?.data) {
          const parseKeyForData = `reportanincident.config`;
          const reportAnIncidentConfig = response.data[parseKeyForData];
          if (Array.isArray(reportAnIncidentConfig) && reportAnIncidentConfig.length > 0) {
            this.appStateBrandReportConfig = reportAnIncidentConfig;
            const codeValues = getActiveCodeValuesByPath(reportAnIncidentConfig, 'dynamicConfig');
            let dynamicConfig = [];
            if (Array.isArray(codeValues) && codeValues.length > 0 && codeValues[0]?.value) {
              try {
                dynamicConfig = JSON.parse(codeValues[0].value);
              } catch {
                dynamicConfig = [];
              }
            }
            this.dynamicConfig = dynamicConfig;
            return true;
          }
        }
        return new Error('Config API returned invalid data');
      })
      .catch(() => new Error('Config API failed'));
  };

  attachLogosToCodeValues = (codeValues, logos, stateCode) => {
    const logoList = logos[stateCode.toUpperCase() || stateCode.toLowerCase()] || [];
    return codeValues.map((codeValue) => {
      const logoObj = logoList.find((logo) => logo?.id === codeValue?.code);
      return logoObj ? { ...codeValue, logoURL: logoObj.logo } : codeValue;
    });
  };

  filterBrandsBasedOnState = (appStateBrandReportConfig, stateObj) => {
    const stateBasedBrandList = getActiveCodeValuesByPath(
      appStateBrandReportConfig,
      `brandlist-${stateObj?.code?.toLowerCase()}`
    );
    const updateBrandDetailsList = this.attachLogosToCodeValues(
      stateBasedBrandList,
      this.logos,
      stateObj?.code?.toLowerCase()
    );
    return updateBrandDetailsList;
  };

  filterReportTypesForStateAndBrand = (appStateBrandReportConfig, stateObj, brandValue) => {
    const reportsListForStateBrand = getActiveCodeValuesByPath(
      appStateBrandReportConfig,
      `reporttypes-${stateObj?.code?.toLowerCase()}-${brandValue?.toLowerCase()}`
    );
    return reportsListForStateBrand;
  };

  async getTemplate() {
    let config = {};

    const { state, code, type, spaceId, appId } = this.brand;

    if (this.inPublicfacingFlow) {
      const publicFlowConfig = this.dynamicConfig[state?.code]?.public ?? [];

      let payerSpecificConfig = {};
      const defaultConfigs = [];
      const otherConfigs = [];

      // defaultConfigs: contains objects where isdefault is true
      // otherConfigs: contains the rest

      for (const config of publicFlowConfig || []) {
        if (config.isDefault) {
          defaultConfigs.push(config);
        } else {
          otherConfigs.push(config);
        }
      }

      if (otherConfigs.length === 1) {
        payerSpecificConfig = otherConfigs[0] || {};
      } else if (otherConfigs.length > 1 && code) {
        payerSpecificConfig =
          otherConfigs.find((obj) => obj.payerIdentifier?.toLowerCase() === code?.toLowerCase()) || {};
      }
      config = { ...(defaultConfigs.length === 1 ? defaultConfigs[0] : {}), ...payerSpecificConfig };

      this.updateBrandDetails('code', code || config.payerIdentifier || '');
      this.updateBrandDetails('name', config.planName || '');
    } else {
      const configArr = this.dynamicConfig[state]?.[spaceId] ?? [];
      const defaultConfig = configArr.find((item) => item.isDefault) || configArr[0] || {};
      const appConfig = configArr.find((item) => item.appId === appId) || {};
      config = { ...defaultConfig, ...appConfig };
    }

    if (Object.keys(config).length > 0) {
      this.assignConfig(config);
    }

    let response = null;

    if (this.config?.templateName) {
      try {
        if (type) {
          response = await import(`../../templates/FHIR-${this.config.templateName}-${type}.json`);
        }
      } catch {
        response = '';
      }
      if (!response) {
        try {
          response = await import(`../../templates/FHIR-${this.config.templateName}.json`);
        } catch {
          response = '';
        }
      }
    } else if (window.location.hostname === 'localhost') {
      const base = `../../templates/FHIR${this.inPublicfacingFlow ? '-public' : ''}-template-MO`;

      try {
        if (type) {
          response = await import(`${base}-${type}.json`);
        }
      } catch {
        response = '';
      }
      if (!response) {
        try {
          response = await import(`${base}.json`);
        } catch {
          response = '';
        }
      }
    }

    if (response) {
      this.templates = response.default;
    }

    return true;
  }

  extractBrandAndReportDeatils = ({ stateObj = {}, brandList = [], code = '', reportsList = [] }) => {
    let brandValue = '';
    let logoURL = '';
    let reportType = '';

    const isStateObjEmpty = !stateObj || Object.keys(stateObj).length === 0;

    if (!isStateObjEmpty && !code && brandList.length === 1) {
      brandValue = brandList[0]?.code || '';
      logoURL = brandList[0]?.logoURL || '';
      const reportTypes = this.filterReportTypesForStateAndBrand(this.appStateBrandReportConfig, stateObj, brandValue);
      this.reportTypes = reportTypes;
      this.brandsByState = brandList;
      if (reportTypes.length === 1) {
        reportType = reportTypes[0]?.code;
      }
      this.brand = {
        ...this.brand,
        state: stateObj,
        code: brandValue.toLowerCase() || '',
        type: reportType.toLowerCase() || '',
        logoURL,
      };
    } else if (code && isStateObjEmpty && reportsList.length === 1) {
      reportType = reportsList[0]?.code || '';
      this.reportTypes = reportsList;
      this.brand = {
        ...this.brand,
        type: reportType.toLowerCase() || '',
      };
    }
  };

  setupPublicFacingFlow = () => {
    this.inPublicfacingFlow = true;
    let brandsByStateList = [];
    let reportTypes = [];
    let reportType = '';
    let brandValue = '';
    let logoURL = '';
    const { stateCode = '', brandCode = '', type = '' } = urlSearch();

    const statesList = getActiveCodeValuesByPath(this.appStateBrandReportConfig, 'states');

    this.states = statesList;

    if (stateCode) {
      const stateObj = statesList.find((state) => state?.code?.toLowerCase() === stateCode.toLowerCase()) || {};
      brandsByStateList = this.filterBrandsBasedOnState(this.appStateBrandReportConfig, stateObj);
      if (!brandCode) {
        if (brandsByStateList.length === 1) {
          brandValue = brandsByStateList[0]?.code || '';
          logoURL = brandsByStateList[0]?.logoURL || '';
          reportTypes = this.filterReportTypesForStateAndBrand(this.appStateBrandReportConfig, stateObj, brandValue);
          if (reportTypes.length === 1) {
            reportType = reportTypes[0]?.code;
          }
        }
      } else if (brandCode && !type) {
        const matchedBrand = brandsByStateList.find((brand) => brand.code.toLowerCase() === brandCode.toLowerCase());
        brandValue = matchedBrand ? matchedBrand.code : '';
        logoURL = matchedBrand ? matchedBrand.logoURL : '';
        if (brandValue) {
          reportTypes = this.filterReportTypesForStateAndBrand(this.appStateBrandReportConfig, stateObj, brandValue);
          if (reportTypes.length === 1) {
            reportType = reportTypes[0]?.code || '';
          }
        }
      } else if (brandCode && type) {
        const matchedBrand = brandsByStateList.find((brand) => brand.code.toLowerCase() === brandCode.toLowerCase());
        brandValue = matchedBrand ? matchedBrand.code : '';
        logoURL = matchedBrand ? matchedBrand.logoURL : '';
        if (brandValue) {
          reportTypes = this.filterReportTypesForStateAndBrand(this.appStateBrandReportConfig, stateObj, brandValue);
          if (reportTypes.length === 1) {
            reportType = reportTypes[0]?.code || '';
          } else {
            reportType = reportTypes.some((reportType) => reportType.code.toLowerCase() === type.toLowerCase())
              ? type
              : '';
          }
        }
      }

      this.brand = {
        ...this.brand,
        state: stateObj,
        code: brandValue.toLowerCase() || '',
        type: reportType.toLowerCase() || '',
        logoURL,
      };
      this.brandsByState = brandsByStateList;
      this.reportTypes = reportTypes;
      this.getTemplate();
    }
  };

  resetStore = () => {
    if (this.inPublicfacingFlow) {
      this.appName = '';
      this.brand = {};
      this.config = {};
      this.templates = {};
      this.dynamicFieldsConfig = [];
      this.brandsByState = [];
      this.reportTypes = [];
    }
  };

  updateBrandDetails = (key, value) => {
    this.brand = { ...this.brand, [key]: value };
  };

  handleChangeCommonStore = (key, value) => {
    if (!key && !value) {
      return;
    }
    this[key] = value;
  };
}

export default new CommonStore();
